/**
 * CreateTicketDialog.tsx — "Let Shield Fix It For Me" dialog
 *
 * Opens when a customer clicks the ticket button on a finding or Shadow AI event.
 * All fields are pre-filled and read-only; the customer can only add a note.
 * On submit, the backend auto-generates the full ticket via AI.
 */

import React, { useState } from 'react';
import axios from 'axios';
import API_BASE from '../config/api';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CreateTicketDialogProps {
  open:        boolean;
  onClose:     () => void;
  customerId:  string;
  /** Pass for finding-based tickets */
  findingId?:  string;
  /** Pass for Shadow AI event-based tickets */
  shadowAiEventId?: string;
  /** Pre-filled display fields (read-only) */
  previewTitle:    string;
  previewPriority: string;
  previewProvider: string;
  previewResource: string;
  previewScore:    number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const PRIORITY_COLOR: Record<string, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
  critical: 'error',
  high:     'error',
  medium:   'warning',
  low:      'success',
};

const PROVIDER_LABELS: Record<string, string> = {
  aws:        'AWS',
  gcp:        'Google Cloud',
  azure:      'Azure',
  ssh:        'Private Server',
  all:        'All Providers',
  shadow_ai:  'Shadow AI',
};

const SLA_LABELS: Record<string, string> = {
  critical: '4 hours',
  high:     '24 hours',
  medium:   '72 hours',
  low:      '7 days',
};

// ── Component ──────────────────────────────────────────────────────────────────

export default function CreateTicketDialog({
  open,
  onClose,
  customerId,
  findingId,
  shadowAiEventId,
  previewTitle,
  previewPriority,
  previewProvider,
  previewResource,
  previewScore,
}: CreateTicketDialogProps) {
  const [note,          setNote]          = useState('');
  const [submitting,    setSubmitting]    = useState(false);
  const [error,         setError]         = useState<string | null>(null);
  const [createdTicket, setCreatedTicket] = useState<{ ticket_number: string; priority: string } | null>(null);

  const handleClose = () => {
    setNote('');
    setError(null);
    setCreatedTicket(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!customerId) {
      setError('No customer selected.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      let res;
      if (findingId) {
        res = await axios.post(
          `${API_BASE}/v1/tickets/create/finding/${findingId}`,
          { customer_id: customerId, customer_note: note }
        );
      } else if (shadowAiEventId) {
        res = await axios.post(
          `${API_BASE}/v1/tickets/create/shadow-ai/${shadowAiEventId}`,
          { customer_id: customerId, customer_note: note }
        );
      } else {
        setError('No finding or event ID provided.');
        return;
      }
      setCreatedTicket({ ticket_number: res.data.ticket_number, priority: res.data.priority });
    } catch (err: any) {
      setError(err.response?.data?.detail ?? err.message ?? 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const slaTime = SLA_LABELS[previewPriority] ?? '72 hours';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {/* ── Success state ─────────────────────────────────────────────────── */}
      {createdTicket ? (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" />
            Ticket Created Successfully
          </DialogTitle>
          <DialogContent>
            <Box textAlign="center" py={2}>
              <Typography variant="h5" fontWeight={700} color="primary" gutterBottom>
                {createdTicket.ticket_number}
              </Typography>
              <Chip
                label={createdTicket.priority.toUpperCase()}
                color={PRIORITY_COLOR[createdTicket.priority] ?? 'default'}
                sx={{ fontWeight: 700, mb: 2 }}
              />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Our security engineer will contact you within <strong>{slaTime}</strong>.
              </Typography>
              <Typography variant="body2" color="text.disabled" mt={1}>
                You can track this ticket in the Tickets section of Shield.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} variant="contained" fullWidth>
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          {/* ── Form state ───────────────────────────────────────────────── */}
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
            <ConfirmationNumberIcon color="primary" />
            Let Shield Fix This For You
          </DialogTitle>

          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Read-only pre-filled summary */}
            <Box
              sx={{
                bgcolor: 'background.default',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                mb: 3,
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                AUTO-FILLED BY SHIELD
              </Typography>

              <Stack spacing={1.5}>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80, pt: 0.3 }}>
                    Issue
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>{previewTitle}</Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                    Priority
                  </Typography>
                  <Chip
                    label={previewPriority.toUpperCase()}
                    color={PRIORITY_COLOR[previewPriority] ?? 'default'}
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                    Platform
                  </Typography>
                  <Chip
                    label={PROVIDER_LABELS[previewProvider] ?? previewProvider.toUpperCase()}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                {previewResource && (
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80, pt: 0.3 }}>
                      Resource
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      {previewResource}
                    </Typography>
                  </Box>
                )}

                {previewScore > 0 && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                      Risk Score
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={previewScore >= 75 ? 'error.main' : previewScore >= 40 ? 'warning.main' : 'text.secondary'}
                    >
                      {previewScore}/100{' '}
                      <Typography component="span" variant="caption" color="text.disabled">
                        (higher = more risk)
                      </Typography>
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Customer note */}
            <TextField
              label="Any additional context? (optional)"
              placeholder="e.g. This bucket contains customer invoices from 2024. We need it working by Monday."
              multiline
              rows={3}
              fullWidth
              value={note}
              onChange={(e) => setNote(e.target.value)}
              variant="outlined"
              helperText="Help our engineer understand the urgency and context. Not required."
            />
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 1 }}>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <ConfirmationNumberIcon />}
              sx={{ fontWeight: 700, py: 1.5 }}
            >
              {submitting ? 'Creating ticket…' : 'Submit — Shield Will Fix This For Me'}
            </Button>
            <Button onClick={handleClose} fullWidth color="inherit" disabled={submitting}>
              Cancel
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
